import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { serverMessage } from '~/lib/api/server-error';
import { useCustomerGroupExpression, useSetCustomerGroupExpression } from '~/hooks/use-customer-groups';
import { CriteriaBuilder, type CriteriaNode } from './criteria-builder';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	groupId: number | null;
	groupName?: string;
}

/** Fullscreen criteria editor for a customer group's dynamic membership expression. */
export function CriteriaDialog({ open, onOpenChange, groupId, groupName }: Props) {
	const { t } = useTranslation();
	const { data: loaded, isLoading } = useCustomerGroupExpression(open ? groupId : null);
	const setExpression = useSetCustomerGroupExpression();
	const [value, setValue] = useState<CriteriaNode>({});

	useEffect(() => {
		if (open && loaded !== undefined) setValue((loaded as CriteriaNode) ?? {});
	}, [open, loaded]);

	const save = async () => {
		if (!groupId) return;
		try {
			await setExpression.mutateAsync({ id: groupId, expression: value });
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-[95vw] sm:max-w-4xl">
				<DialogHeader>
					<DialogTitle>{t('customers.groups.criteria.title')}</DialogTitle>
					<DialogDescription>{groupName || t('customers.groups.criteria.subtitle')}</DialogDescription>
				</DialogHeader>
				<div className="max-h-[65vh] overflow-auto rounded-md border border-border bg-surface p-3">
					{isLoading ? (
						<div className="flex flex-col gap-2">
							{Array.from({ length: 4 }).map((_, i) => (
								<Skeleton key={i} className="h-9 w-full rounded-md" />
							))}
						</div>
					) : (
						<CriteriaBuilder value={value} onChange={setValue} />
					)}
				</div>
				<DialogFooter>
					<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
						{t('common.cancel')}
					</Button>
					<Button type="button" onClick={save} disabled={setExpression.isPending}>
						{setExpression.isPending ? t('common.saving') : t('common.save')}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
